<script>
import axios from '@/utils/axios';
import { useLeadMetaStore } from '@/stores/useLeadMetaStore';

const DEFAULT_FORM = () => ({
  id: null,
  branch_id: '',
  free_lates_per_month: 0,
  late_to_halfday_count: '',
  halfday_to_absent_count: '',
  effective_from: '',
  effective_to: '',
});

export default {
  inject: ['setLoading'],
  emits: ['updated'],
  data() {
    return {
      leadMeta: useLeadMetaStore(),
      form: DEFAULT_FORM(),
      errors: {},
      deleteItem: null,
    };
  },
  computed: {
    branches() {
      return this.leadMeta.branches || [];
    },
  },
  mounted() {
    // sirf branches chahiye; store me na ho to sirf branches fetch karo
    if (!this.leadMeta.branches || !this.leadMeta.branches.length) {
      this.leadMeta.refreshBranches();
    }
  },
  methods: {
    branchLabel(b) {
      return b.name || b.branch_name || b.title || `Branch #${b.id}`;
    },

    // ISO "2026-06-14T18:30:00.000000Z" -> "2026-06-14" (date input format)
    toInputDate(val) {
      if (!val) return '';
      return String(val).slice(0, 10);
    },

    add() {
      this.resetForm();
    },

    edit(policy) {
      this.resetForm();
      this.form.id = policy.id;
      this.form.branch_id = policy.branch_id || '';
      this.form.free_lates_per_month = policy.free_lates_per_month ?? 0;
      this.form.late_to_halfday_count = policy.late_to_halfday_count ?? '';
      this.form.halfday_to_absent_count = policy.halfday_to_absent_count ?? '';
      this.form.effective_from = this.toInputDate(policy.effective_from);
      this.form.effective_to = this.toInputDate(policy.effective_to);
    },

    setDelete(policy) {
      this.deleteItem = policy;
    },

    buildPayload() {
      return {
        branch_id: this.form.branch_id || null,
        free_lates_per_month: this.form.free_lates_per_month === '' ? 0 : Number(this.form.free_lates_per_month),
        late_to_halfday_count: this.form.late_to_halfday_count,
        halfday_to_absent_count: this.form.halfday_to_absent_count,
        effective_from: this.form.effective_from || null,
        effective_to: this.form.effective_to || null,  
      };
    },

    async save() {
      this.errors = {};
      this.setLoading(true);
      try {
        let response;
        if (this.form.id) {
          response = await axios.put(`/hrms/attendance-policies/${this.form.id}`, this.buildPayload());
        } else {
          response = await axios.post('/hrms/attendance-policies', this.buildPayload());
        }
        this.$swalToast(response.data.message);
        this.$emit('updated');
        this.$closeModal('hrm_attendance_policy_modal');
        this.resetForm();
      } catch (error) {
        if (error.type === 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async deletePolicy() {
      this.setLoading(true);
      try {
        const response = await axios.delete(`/hrms/attendance-policies/${this.deleteItem.id}`);
        this.$swalToast(response.data.message);
        this.$emit('updated');
        this.$closeModal('hrm_attendance_policy_delete_modal');
      } catch (error) {
        if (error.type === 'validation') {
          this.$swalToast(error.errors?.id?.[0] || error.message, 'error');
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    resetForm() {
      this.form = DEFAULT_FORM();
      this.errors = {};
    },
  },
};
</script>

<template>
  <!-- Add/Edit Modal -->
  <div class="modal fade" id="hrm_attendance_policy_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ form.id ? 'Edit' : 'Add' }} Attendance Policy</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <form @submit.prevent="save">
          <div class="modal-body pb-0">

            <!-- Branch -->
            <div class="mb-3">
              <label class="form-label">Branch <span class="text-danger">*</span></label>
              <select class="form-select" v-model="form.branch_id">
                <option value="">Select branch</option>
                <option v-for="b in branches" :key="b.id" :value="b.id">{{ branchLabel(b) }}</option>
              </select>
              <div v-if="errors.branch_id" class="text-danger small mt-1">{{ errors.branch_id[0] }}</div>
            </div>

            <!-- Free lates per month -->
            <div class="mb-3">
              <label class="form-label">Free Lates / Month</label>
              <input type="number" min="0" step="1" class="form-control" v-model.number="form.free_lates_per_month" placeholder="e.g. 3" />
              <small class="text-muted" style="font-size:10.5px;">No. of late marks allowed per month before the Late → Half-day rule starts counting</small>
              <div v-if="errors.free_lates_per_month" class="text-danger small mt-1">{{ errors.free_lates_per_month[0] }}</div>
            </div>

            <div class="row">
              <!-- Late to half-day -->
              <div class="col-md-12 mb-3">
                <label class="form-label">Late → Half-day <span class="text-danger">*</span></label>
                <input type="number" min="1" class="form-control" v-model="form.late_to_halfday_count" placeholder="e.g. 3" />
                <small class="text-muted" style="font-size:10.5px;">No. of lates that count as a half-day</small>
                <div v-if="errors.late_to_halfday_count" class="text-danger small mt-1">{{ errors.late_to_halfday_count[0] }}</div>
              </div>

              <!-- Half-day to absent -->
              <div class="col-md-6 mb-3 d-none">
                <label class="form-label">Half-day → Absent <span class="text-danger">*</span></label>
                <input type="number" min="1" class="form-control" v-model="form.halfday_to_absent_count" placeholder="e.g. 2" />
                <small class="text-muted" style="font-size:10.5px;">No. of half-days that count as absent</small>
                <div v-if="errors.halfday_to_absent_count" class="text-danger small mt-1">{{ errors.halfday_to_absent_count[0] }}</div>
              </div>
            </div>

            <div class="row">
              <!-- Effective from -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Effective From <span class="text-danger">*</span></label>
                <input type="date" class="form-control" v-model="form.effective_from" />
                <div v-if="errors.effective_from" class="text-danger small mt-1">{{ errors.effective_from[0] }}</div>
              </div>

              <!-- Effective to -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Effective To</label>
                <input type="date" class="form-control" v-model="form.effective_to" />
                <small class="text-muted" style="font-size:10.5px;">Leave empty if ongoing</small>
                <div v-if="errors.effective_to" class="text-danger small mt-1">{{ errors.effective_to[0] }}</div>
              </div>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="btn btn-primary">{{ form.id ? 'Update' : 'Save' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade" id="hrm_attendance_policy_delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3">You want to delete this attendance policy? This can't be undone.</p>
          <div class="d-flex justify-content-center">
            <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">Cancel</a>
            <a href="javascript:void(0);" class="btn btn-danger" @click="deletePolicy">Yes, Delete</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>